// FlowHeader — page chrome: brand, a LIVE badge (staleness rule wired but
// dormant while the feed is historic), and the window / trade-count / as-of
// strip from the payload meta. The token selector is its own control below the
// header (TokenSelector), driven by the embedded manifest.

import { Box, Stack } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import { fmtCount, isFeedStale } from '../../../../providers/options-flow/options-flow.helpers';
import { FlowReportMeta } from '../../../../providers/options-flow/types';

interface HeaderMeta {
  generated_utc: string;
  currency: string;
  venue: string;
  cadence_sec: number | null;
}

interface FlowHeaderProps {
  headerMeta: HeaderMeta | null;
  windowMeta: FlowReportMeta | null;
}

export const FlowHeader = ({ headerMeta, windowMeta }: FlowHeaderProps) => {
  // LIVE when not stale; dormant (always LIVE) while historic (cadence null).
  const isLive = headerMeta
    ? !isFeedStale(headerMeta.generated_utc, headerMeta.cadence_sec)
    : false;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ flexWrap: 'wrap', gap: 1.5 }}
    >
      <Box
        sx={{ fontSize: 16, fontWeight: 800, letterSpacing: '0.3px', color: tokens.text.primary }}
      >
        IMMERSIVE{' '}
        <Box component="span" sx={{ color: tokens.flow.buy }}>
          ·
        </Box>{' '}
        Options Flow
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {isLive && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: tokens.status.success,
              backgroundColor: tokens.status.successBg,
              border: `1px solid ${tokens.status.successBorder}`,
              borderRadius: '20px',
              padding: '4px 11px',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: tokens.status.success,
              }}
            />
            LIVE
          </Box>
        )}

        {windowMeta && (
          <Box
            sx={{
              color: tokens.text.muted,
              fontSize: 11,
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            }}
          >
            window{' '}
            <Box component="span" sx={{ color: tokens.text.secondary, fontWeight: 600 }}>
              {windowMeta.window_from} → {windowMeta.window_to}
            </Box>
            {'  |  '}
            <Box component="span" sx={{ color: tokens.text.secondary, fontWeight: 600 }}>
              {fmtCount(windowMeta.n_trades)}
            </Box>{' '}
            trades{'  |  '}as-of{' '}
            <Box component="span" sx={{ color: tokens.text.secondary, fontWeight: 600 }}>
              {headerMeta?.generated_utc ?? windowMeta.generated_utc}
            </Box>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
